import { AppLayout } from '~/components/layout/AppLayout'
import { AuthProvider } from '~/contexts/AuthContext'
import { LicenseInfo } from '@mui/x-license'
import { NextPage } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { ReactElement, ReactNode } from 'react'
import { AppApolloProvider } from '~/features/provider/AppApolloProvider'
import { useReloadAfterOneDay } from '~/lib/hooks/app/useReloadAfterOneDay'
import { useTagManagerInit } from '~/lib/hooks/app/useTagManagerInit'
import { BugsnagErrorBoundary } from '~/components/error_boundary/BugsnagErrorBoundary'
import { AppSnackbarProvider } from '~/features/provider/AppSnackbarProvider'
import { AppThemeProvider } from '~/features/provider/AppThemeProvider'
import { FacebookSDK } from '~/components/common/Facebook/FacebookSDK'
import { TopProgress } from '~/components/TopProgress'
import { CustomErrorPage } from '~/components/common/CustomErrorPage'
import { UNDER_MAINTENANCE } from '~/constants/maintenance'
import { WebpushProvider } from '~/contexts/WebpushContext'
import { LiteracySurvey } from '~/components/common/Enquate/LiteracySurvey'
import { APP_ENV, MUI_LICENSE_KEY } from '~/lib/config'
import Script from 'next/script'
// import { initWebVitalsReporting } from '~/lib/web-vitals'
import { initializeDatadog } from '~/lib/datadog'
import { logger } from '~/lib/logger'

import { DateTimeProvider } from '~/contexts/DateTimeContext'
import { theme } from '~/lib/theme'
import { ApiConfigProvider } from '~/lib/api-config/provider'

if (typeof window !== 'undefined') {
  try {
    initializeDatadog()

    // initWebVitalsReporting()
  } catch (error) {
    logger.error(new Error('Failed to initialize on client', { cause: error }))
  }
}

// ローカルの場合はmswを読み込む
// eslint-disable-next-line @typescript-eslint/no-require-imports
if (APP_ENV === 'local') require('~/mocks/msw')
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY)

// Provider類を全てネストさせたコンポーネント
const providers = [
  BugsnagErrorBoundary,
  ApiConfigProvider,
  AppApolloProvider,
  AppSnackbarProvider,
  AuthProvider,
  WebpushProvider,
  AppThemeProvider,
  DateTimeProvider,
]

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const CompositeProvider = ({ children }: { children: ReactNode }) => (
  <>
    {providers.reduceRight(
      (children, Provider) => (
        <Provider>{children}</Provider>
      ),
      children,
    )}
  </>
)

const MaintenanceChecker = ({ children }: { children: ReactNode }) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- UNDER_MAINTENANCE を true にするとメンテナンス画面へ切り替わる
  if (UNDER_MAINTENANCE) {
    return (
      <>
        <Head>
          <title>システムメンテナンス中 | toridori marketing</title>
        </Head>
        <CustomErrorPage
          errorTitle="現在システムメンテナンス中のため、サービスをご利用いただけません。"
          errorDetail={'システムメンテナンスが終了次第、再度ご利用ください。'}
          showTopLink={false}
        />
      </>
    )
  }

  return <>{children}</>
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  useReloadAfterOneDay()
  useTagManagerInit()

  /**
   * NOTE: Pagesコンポーネントにて、LayoutプロパティにAppLayout以外のレイアウトを指定することで、
   *        レイアウトを変更することができる。
   *        現状、AppLayoutを外したいユースケースにて、LayoutWithNothingコンポーネントを指定することで
   *        サイドメニューやヘッダの必要ないログイン画面等を標示している。
   *        なお、明示的にLayoutプロパティを指定しなかった場合は、AppLayoutが適用される。
   */
  const Layout = (Component as { Layout?: React.ComponentType }).Layout || AppLayout

  // https://nextjs.org/docs/pages/building-your-application/routing/pages-and-layouts#with-typescript
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <Head>
        <title>toridori marketing</title>
        <meta name="viewport" content="minimum-scale=1, maximum-scale=1, initial-scale=1, width=device-width" />
      </Head>

      <FacebookSDK />
      <CompositeProvider>
        <MaintenanceChecker>
          <TopProgress />
          <LiteracySurvey />
          <Layout>{getLayout(<Component {...pageProps} />)}</Layout>
          <TopProgress />
        </MaintenanceChecker>
      </CompositeProvider>
      <Script id="techtouch" strategy="lazyOnload">
        {techtouchScript}
      </Script>

      {/* Tayori チャットボット */}
      <Script id="tayori" strategy="lazyOnload">
        {tayoriScript}
      </Script>

      {/* Tayoriチャットボットの位置調整 */}
      <style jsx global>{`
        /* SPのみに位置調整を適用（MUIのmdブレークポイントに合わせる） */
        @media (max-width: 899px) {
          #tayori-ai-chatbot-app {
            bottom: 80px !important; /* デフォルトの20pxから80pxに変更 */
            margin-bottom: 20px !important;
            margin-right: 20px !important;
          }

          /* iOS Safariで高さを正確に計算できるようにvhではなくdvhで計算 */
          .tayori-ai-chatbot-app-chat {
            height: calc(-150px + 100dvh) !important;
          }

          /* スマホでのみテキスト部分を非表示 */
          .tayori-ai-chatbot-app-tayori-ai-chatbot-text-content {
            display: none !important;
          }

          /* モバイルでのみボディ部分を50pxに調整 */
          .tayori-ai-chatbot-app-tayori-ai-chatbot-text-body {
            width: 55px !important;
            height: 55px !important;
          }
        }
        /* PCの場合に、ドロワー上のコンテンツとかぶらないようにz-indexを下げて、ドロワーオープン時はチャットボットがドロワーの背後に表示されるようにする */
        @media (min-width: 900px) {
          #tayori-ai-chatbot-app {
            z-index: ${theme.zIndex.drawer - 1} !important;
          }
        }
      `}</style>
    </>
  )
}

const techtouchScript = `
!function () {
  // iframe内で動作させる場合は以下の1行を削除もしくはコメントアウトしてください
  try { if (window.top !== window.self) return; } catch(e) { return; }
  if ('TechtouchObject' in window && document.querySelector('script#techtouch-snippet')) return;
  if ('TechtouchAddon' in window) return;
  window.TechtouchObject = {
    organizationUuid: "orga-65f00809-63bb-f37c-1cc5-b88deb826f70"
  };
  var e = document.createElement("script"); e.async = 1, e.src = "https://apps.techtouch.jp/script/orga-65f00809-63bb-f37c-1cc5-b88deb826f70/main.js?projectUuid=proj-65f3b3bb-fe8b-a088-1530-5f04dc14df91"; e.id = "techtouch-snippet";
  var t = document.getElementsByTagName("script")[0]; t.parentNode.insertBefore(e, t);
}()
`

const tayoriScript = `
(function() {a=document.getElementsByTagName('script')[0];d= document.createElement('script');d.type='text/javascript';d.async=1;d.src='https://ai-chatbot.tayori.com/assets/chat/index.js?key=9f20734e-ff5e-4a24-b53f-2bf58dae4460';a.parentNode.insertBefore(d,a)})();
`
